import { Alert, Code, Group, List, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AnalysisStaff } from '@/api/analyses';
import type { DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';
import { InternalStateChip } from './InternalStateChip';

/** How many listed files the preview names before it says "and N more". */
const SHOWN_FILES = 8;

interface Listing {
  files?: { path: string; size_bytes: number }[];
  file_count?: number;
}

/** What a rehearsed (`dry_run`) attach would make of the object: where its data is, how much, in what state. */
export function ObjectPreview({ object }: { object: DataObject }) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const listing = (object.listing_snapshot ?? {}) as Listing;
  const files = listing.files ?? [];
  const processed = !!object.processed_uri;

  return (
    <Alert color="forest" variant="light" title={t('referenced.previewTitle')} data-testid="object-preview">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fz={14} fw={600}>
            {kindLabel(object.kind)} · {objectName(object)}
          </Text>
          <InternalStateChip state={object.state ?? ''} />
        </Group>
        <Table withRowBorders={false} verticalSpacing={2} fz={13}>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td c={brand.textMuted}>{t('referenced.rawData')}</Table.Td>
              <Table.Td>
                <Code>{object.raw_uri}</Code> · {bytes(object.raw_bytes ?? 0)}
              </Table.Td>
            </Table.Tr>
            {processed && (
              <Table.Tr>
                <Table.Td c={brand.textMuted}>{t('referenced.processedData')}</Table.Td>
                <Table.Td>
                  <Code>{object.processed_uri}</Code> · {bytes(object.total_bytes ?? 0)}
                </Table.Td>
              </Table.Tr>
            )}
            {object.crs && (
              <Table.Tr>
                <Table.Td c={brand.textMuted}>CRS</Table.Td>
                <Table.Td>{object.crs}</Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        <Text fz={13} c={brand.textMuted}>
          {t(processed ? 'referenced.processedFiles' : 'referenced.files', { count: object.file_count ?? files.length })}
        </Text>
        <List size="sm" spacing={0} ff="monospace">
          {files.slice(0, SHOWN_FILES).map((f) => (
            <List.Item key={f.path}>
              {f.path} · {bytes(f.size_bytes)}
            </List.Item>
          ))}
        </List>
        {files.length > SHOWN_FILES && (
          <Text fz={12} c={brand.textMuted}>
            {t('referenced.moreFiles', { count: files.length - SHOWN_FILES })}
          </Text>
        )}
        <Text fz={13}>{t(object.state === 'ready' ? 'referenced.willBeReady' : 'referenced.willBeVerified')}</Text>
      </Stack>
    </Alert>
  );
}

/**
 * What a rehearsed attach would do to an analysis: its state before and after, and the outputs it would
 * gain (`added`), each internal or for the client.
 */
export function AnalysisChangePreview({ before, after, added }: { before: AnalysisStaff; after: AnalysisStaff; added: string[] }) {
  const { t, i18n } = useTranslation('projects');
  const kindLabel = useKindLabel();
  const lang = i18n.language as 'cs' | 'en';
  const outputs = (after.all_outputs ?? []).filter((o) => added.includes(o.public_id));
  const moved = before.state !== after.state;
  return (
    <Alert color="forest" variant="light" title={t('referenced.analysisPreviewTitle')} data-testid="analysis-preview">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fz={14} fw={600}>
            {after.name[lang] || after.name.cs}
          </Text>
          <InternalStateChip state={before.state ?? ''} />
          {moved && (
            <>
              <Text fz={13}>→</Text>
              <InternalStateChip state={after.state ?? ''} />
            </>
          )}
        </Group>
        <Text fz={13} c={brand.textMuted}>
          {t(moved ? 'referenced.stateMoves' : 'referenced.stateStays')}
        </Text>
        <List size="sm" spacing={2}>
          {outputs.map((o) => (
            <List.Item key={o.public_id}>
              {kindLabel(o.kind)} · {o.name || o.declared_name || o.public_id} ·{' '}
              <Text span fz={13} c={o.internal ? brand.textMuted : undefined} fw={o.internal ? 400 : 600}>
                {t(o.internal ? 'referenced.internalOutput' : 'referenced.clientOutput')}
              </Text>
            </List.Item>
          ))}
        </List>
      </Stack>
    </Alert>
  );
}
